/*
 * 消息朗读的编排。
 *
 * ## 为什么是一个 hook 而不是每个按钮各管各的
 *
 * **同一时刻只能有一条消息在响。** 各管各的话，点第二条时第一条还在放，
 * 两个缈缈的声音叠在一起。所以播放状态提到一处，切换时先停旧的。
 *
 * ## iOS 的坑：play() 必须在用户手势的同步调用栈里
 *
 * Safari/WKWebView 只允许由用户手势直接触发的 `play()`。如果先 await 一个
 * 网络请求再 play，手势上下文已经丢了，播放会被静默拒绝——
 * 旧项目就栽在这里（`voice.ts:142` 的 `.catch(() => console.log('播放失败'))`）。
 *
 * 对策：**在点击的同步路径里就创建并"点燃" Audio 元素**，
 * 之后再把真实的 src 塞进去。见 `ensureUnlocked`。
 *
 * ## 计费
 *
 * 一条消息一生只合成一次、扣一次灵力，之后重播免费
 * （见 docs/ECONOMY.md §6）。所以这里必须区分：
 *   idle  -> 点击会触发合成，**要花钱**
 *   ready -> 点击只是重播，不花钱
 * 已经拿到过 URL 的消息缓存在 `urls` 里，页面存续期内不再请求。
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { SpeakState } from "../components/SpeakButton";

/** 服务端返回的朗读结果。 */
interface SpeakResult {
  /** 我方转存后的音频地址，不是供应商的临时 URL。 */
  url: string;
  durationMs?: number;
}

/*
 * 50ms、8kHz、8-bit mono PCM 的真正静音 WAV。
 *
 * 不能再拿空 src「解锁」：空资源的 play() 会直接失败，等 TTS 请求回来时
 * 用户手势早已丢失。这里在点击的同步调用栈里让同一个 audio 元素循环播放
 * 一段有效的静音；真实 URL 到手后只换这个元素的 src。WebKit 的播放许可按
 * media element 授予，复用元素正是关键。
 */
const SILENT_AUDIO_SRC =
  "data:audio/wav;base64,UklGRrQBAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YZABAACAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICA";

export interface SpeakController {
  /** 取某条消息当前的状态。没记录过的一律是 idle。 */
  stateOf: (messageId: string) => SpeakState;
  /** 点喇叭。正在响的再点一次就是停。 */
  toggle: (messageId: string) => void;
  /** 当前环境能不能放音频。为 false 时上层不该渲染按钮。 */
  supported: boolean;
}

export function useSpeak({
  fetchAudio,
  supported,
  onError,
}: {
  /** 向服务端要这条消息的语音。首次会触发合成与扣费，之后应命中服务端缓存。 */
  fetchAudio: (messageId: string) => Promise<SpeakResult>;
  supported: boolean;
  onError?: (message: string) => void;
}): SpeakController {
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [failedIds, setFailedIds] = useState<ReadonlySet<string>>(new Set());
  const urls = useRef(new Map<string, string>());
  const audioRef = useRef<HTMLAudioElement | null>(null);
  /* 每次点击递增。旧请求晚回来时只能入缓存，不能抢走当前正在播放的消息。 */
  const operationRef = useRef(0);

  /**
   * 拿到全页唯一的 audio 元素。
   *
   * 播放许可跟着元素走，所以真实音频、静音预热与后续重播都必须复用它。
   */
  const audioElement = useCallback((): HTMLAudioElement => {
    if (!audioRef.current) {
      const el = new Audio();
      el.preload = "auto";
      audioRef.current = el;
    }
    return audioRef.current;
  }, []);

  // 组件卸载时停掉，否则切页面后声音还在响。
  useEffect(() => {
    return () => {
      operationRef.current += 1;
      const el = audioRef.current;
      if (el) {
        el.pause();
        el.onended = null;
        el.onerror = null;
      }
      audioRef.current = null;
    };
  }, []);

  const stop = useCallback(() => {
    operationRef.current += 1;
    const el = audioRef.current;
    if (el) {
      el.pause();
      el.loop = false;
      el.currentTime = 0;
    }
    setPlayingId(null);
  }, []);

  const play = useCallback(
    (messageId: string, url: string, el: HTMLAudioElement, operation: number) => {
      /*
       * 静音预热还在循环时，单纯赋值 src 后立刻 play()，部分浏览器会把这次
       * play 仍结算到旧的静音资源上：Promise 成功，但静音片段随即 ended，
       * 真实语音只完成加载、不继续播放。AVG 模式实测正是 loading -> ready，
       * 第二次点击才 playing。
       *
       * 先停旧资源，再显式 load()，把后面的 play 请求绑定到新 URL；媒体元素
       * 本身没有换，所以首次点击取得的 WebKit 播放许可仍然保留。
       */
      el.pause();
      el.loop = false;
      el.onended = null;
      el.onerror = null;
      el.src = url;
      el.onended = () => {
        if (operationRef.current === operation) setPlayingId(null);
      };
      el.onerror = () => {
        if (operationRef.current !== operation) return;
        setPlayingId(null);
        setFailedIds((s) => new Set(s).add(messageId));
        onError?.("这段语音放不出来，等会儿再试试");
      };
      el.load();
      void el
        .play()
        .then(() => {
          if (operationRef.current === operation) setPlayingId(messageId);
        })
        .catch(() => {
          if (operationRef.current !== operation) return;
          // 走到这里通常是自动播放策略拦截，而不是文件坏了。
          // 如实告知，不静默——旧项目就是在这里把问题吞掉的。
          setPlayingId(null);
          setFailedIds((s) => new Set(s).add(messageId));
          onError?.("当前环境不允许自动播放，再点一次试试");
        });
    },
    [onError],
  );

  const toggle = useCallback(
    (messageId: string) => {
      if (!supported) return;

      if (playingId === messageId) {
        stop();
        return;
      }

      /*
       * 新点击接管唯一的播放器。也会让之前还在合成的请求失去播放资格；
       * 它晚回来时仍会缓存 URL，但不会突然盖过用户后来选的那一条。
       */
      const operation = operationRef.current + 1;
      operationRef.current = operation;
      const el = audioElement();
      el.pause();
      el.loop = false;
      el.currentTime = 0;
      el.onended = null;
      el.onerror = null;
      setPlayingId(null);
      setLoadingId(null);

      setFailedIds((s) => {
        if (!s.has(messageId)) return s;
        const next = new Set(s);
        next.delete(messageId);
        return next;
      });

      const cached = urls.current.get(messageId);
      if (cached) {
        // 已缓存时 play() 仍在本次点击的同步栈内，不需要静音预热。
        play(messageId, cached, el, operation);
        return;
      }

      /*
       * ⚠ 必须在 fetchAudio 之前、趁用户手势还在时调用 play()。
       * 静音资源保持循环，直到真实音频准备好；不能立刻 pause，否则播放许可
       * 还没建立就会被自己撤掉，又退化成「第二次点才响」。
       */
      el.src = SILENT_AUDIO_SRC;
      el.loop = true;
      const unlocked = el.play().catch(() => undefined);

      setLoadingId(messageId);
      void fetchAudio(messageId)
        .then(async (res) => {
          urls.current.set(messageId, res.url);
          await unlocked;
          if (operationRef.current !== operation) return;
          setLoadingId(null);
          play(messageId, res.url, el, operation);
        })
        .catch((e: Error) => {
          if (operationRef.current !== operation) return;
          el.pause();
          el.loop = false;
          setLoadingId(null);
          setFailedIds((s) => new Set(s).add(messageId));
          onError?.(e.message || "语音没能准备好");
        });
    },
    [supported, playingId, stop, audioElement, fetchAudio, play, onError],
  );

  const stateOf = useCallback(
    (messageId: string): SpeakState => {
      if (playingId === messageId) return "playing";
      if (loadingId === messageId) return "loading";
      if (failedIds.has(messageId)) return "failed";
      return urls.current.has(messageId) ? "ready" : "idle";
    },
    [playingId, loadingId, failedIds],
  );

  return { stateOf, toggle, supported };
}
